import React, { useState } from 'react';
import { useLoadFileFormats, useImportLoadFileFormats, LoadFileFormat } from '../../hooks/useLoadFileFormats';
import { useProjects } from '../../hooks/useProjects';
import { LoadingPlaceholder } from '../../components/LoadingPlaceholder';
import { SubmitButton } from '../../components/SubmitButton';

export function ExportsTab({ projectId }: { projectId: string }) {
  const { data: projects = [] } = useProjects();
  const [sourceProjectId, setSourceProjectId] = useState('');
  const [checkedIds, setCheckedIds] = useState<Set<string>>(new Set());

  const { data: formats = [], isLoading: loadingFormats } = useLoadFileFormats(sourceProjectId);
  const importFormats = useImportLoadFileFormats(projectId);

  const userFormats = formats.filter(f => f.type === 'user');
  const otherProjects = projects.filter(p => p.id !== projectId);

  const handleSourceChange = (id: string) => {
    setSourceProjectId(id);
    setCheckedIds(new Set());
  };

  const handleFormatChange = (format: LoadFileFormat, checked: boolean) => {
    setCheckedIds(prev => {
      const next = new Set(prev);
      checked ? next.add(format.id) : next.delete(format.id);
      return next;
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const toImport = userFormats.filter(f => checkedIds.has(f.id));
    if (!toImport.length) return;
    importFormats.mutate(toImport, {
      onSuccess: () => {
        setSourceProjectId('');
        setCheckedIds(new Set());
      },
    });
  };

  const canSubmit = !!sourceProjectId && checkedIds.size > 0 && !importFormats.isPending;

  return (
    <div>
      <h2><i className="fas fa-user" aria-hidden="true" /> Import Load File Formats</h2>
      <form className="form-horizontal" onSubmit={handleSubmit} noValidate>

        <div className="form-group">
          <label className="control-label col-sm-3" htmlFor="sourceProjectId">From Project</label>
          <div className="col-sm-6">
            <select
              required
              className="form-control"
              id="sourceProjectId"
              value={sourceProjectId}
              onChange={e => handleSourceChange(e.target.value)}
            >
              <option value="">-- select a project --</option>
              {otherProjects.map(p => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>
        </div>

        {sourceProjectId && (
          <div className="form-group">
            <label className="control-label col-sm-3">Load File Formats</label>
            <div className="col-sm-6">
              {loadingFormats ? (
                <LoadingPlaceholder />
              ) : userFormats.length > 0 ? (
                userFormats.map(format => (
                  <div className="checkbox checkbox-primary" key={format.id}>
                    <input
                      id={format.id}
                      type="checkbox"
                      checked={checkedIds.has(format.id)}
                      onChange={e => handleFormatChange(format, e.target.checked)}
                    />
                    <label htmlFor={format.id}>{format.name}</label>
                  </div>
                ))
              ) : (
                <div className="alert alert-warning">
                  <strong>Warning:</strong><br />
                  No User-created Load File Formats found
                </div>
              )}
            </div>
          </div>
        )}

        <div className="form-group">
          <div className="col-sm-6 col-sm-offset-3">
            <SubmitButton
              pending={importFormats.isPending}
              pendingLabel="Importing..."
              disabled={!canSubmit}
            >Import</SubmitButton>
          </div>
        </div>

      </form>
    </div>
  );
}
